/**
 * Money movements — the centrepiece.
 *
 * A movement is a JOURNAL with all its lines, never a one-sided balance change, and the lines are shown
 * summing to zero. That is the whole point: a balance without its counter-entry is exactly the view that
 * hides mistakes.
 *
 * Tags carry shape and text, never colour alone — ↺ REVERSAL outlined, FEE solid, ▲ LARGE outlined.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, type Movement } from "../lib/api";
import { useLoader } from "../lib/session";
import { dateTimeLong, money, relative } from "../lib/format";
import { C, card, CardHead, colHead, EmptyState, ErrorState, Skeleton, Tick } from "../ui/primitives";

const KIND_LABEL: Record<string, string> = {
  collection_confirmed: "Collection",
  collection_fee: "Fee",
  payout_initiated: "Payout · initiated",
  payout_confirmed: "Payout · confirmed",
  payout_bounced: "Payout · bounced",
  sweep_confirmed: "Sweep",
};

export function Movements() {
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [extra, setExtra] = useState<Movement[]>([]);

  const q = {
    tenantId: params.get("tenantId") ?? undefined,
    kind: params.get("kind") ?? undefined,
    refId: params.get("refId") ?? undefined,
    cursor,
  };
  const { data, error, loading, reload } = useLoader(() => api.movements(q), [q.tenantId, q.kind, q.refId, cursor]);

  const rows = [...extra, ...(data?.movements ?? [])];
  const setFilter = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setExtra([]); setCursor(undefined); setParams(next);
  };

  return (
    <>
      <div style={card}>
        <div style={{ padding: "13px 16px", borderBottom: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Money movements</div>
            <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginTop: 1, lineHeight: 1.45 }}>
              Every naira through the engine, as balanced journals. A movement is never a one-sided balance change — expand any row to see its lines sum to zero.
            </div>
          </div>
          {/* ONE toolbar row — the house standard. */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 7, alignItems: "center", flex: 1, minWidth: 180, padding: "9px 12px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--subtle)" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
              <input defaultValue={q.refId ?? ""} onKeyDown={(e) => { if (e.key === "Enter") setFilter("refId", (e.target as HTMLInputElement).value.trim()); }}
                placeholder="Reference — press Enter" style={{ border: "none", background: "none", color: "var(--ink)", fontSize: 12.5, fontWeight: 600, flex: 1, minWidth: 0 }} />
            </div>
            <input defaultValue={q.tenantId ?? ""} onKeyDown={(e) => { if (e.key === "Enter") setFilter("tenantId", (e.target as HTMLInputElement).value.trim()); }}
              placeholder="Tenant id" style={sel} />
            <select value={q.kind ?? ""} onChange={(e) => setFilter("kind", e.target.value)} style={sel}>
              <option value="">All types</option>
              {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {(q.tenantId || q.kind || q.refId) && (
              <button type="button" onClick={() => { setExtra([]); setCursor(undefined); setParams(new URLSearchParams()); }} style={{ ...sel, color: C.accent, cursor: "pointer" }}>Clear filters</button>
            )}
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <Skeleton label="Loading movements… rows appear all at once when the query completes — never a partial ledger view." />
        ) : error ? (
          <ErrorState title="Couldn't load movements."
            body="The ledger did not answer. Nothing shown here is partial — we show all of a query's rows or none, because a partial ledger view reads as missing money."
            onRetry={reload} />
        ) : rows.length === 0 ? (
          <EmptyState title="No movements match these filters."
            body="Nothing moved in this window. That can be the correct answer — widen the range or clear a filter to check."
            action={<button type="button" onClick={() => setParams(new URLSearchParams())} style={{ padding: "9px 13px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--surface)", color: C.accent, fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Clear filters</button>} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ ...grid, padding: "9px 16px", borderBottom: "1px solid var(--line)", background: "var(--subtle)" }}>
              <span style={colHead}>WHEN (WAT)</span><span style={colHead}>REFERENCE</span>
              <span style={colHead}>TENANT / TYPE</span><span style={colHead}>FLAGS</span>
              <span style={{ ...colHead, textAlign: "right" }}>AMOUNT</span><span />
            </div>
            {rows.map((m) => <Row key={m.id} m={m} names={data?.tenantNames ?? {}} open={openId === m.id} onToggle={() => setOpenId(openId === m.id ? null : m.id)} />)}
            {data?.nextCursor && (
              <button type="button"
                onClick={() => { setExtra(rows); setCursor(data.nextCursor!); }}
                style={{ width: "100%", padding: 14, border: "none", background: "var(--subtle)", color: C.accent, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                Load 50 earlier movements
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Row({ m, names, open, onToggle }: { m: Movement; names: Record<string, string>; open: boolean; onToggle: () => void }) {
  // The tenant side of a journal is whichever line touches a tenant wallet.
  const tenantLine = m.lines.find((l) => l.tenantId);
  const gross = Math.max(...m.lines.map((l) => Math.abs(l.amount)), 0);
  const isReversal = !!m.reversesId || m.kind.endsWith("_reversal");
  const touchesFee = m.lines.some((l) => l.account === "platform_fees");
  const sum = Math.round(m.lines.reduce((s, l) => s + l.amount, 0) * 100) / 100;

  return (
    <div style={{ borderBottom: "1px solid var(--line)" }}>
      <div onClick={onToggle} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        style={{ ...grid, padding: "12px 16px", alignItems: "center", cursor: "pointer" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>{dateTimeLong(m.postedAt)}</div>
          <div style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 600 }}>{relative(m.postedAt)}</div>
        </div>
        <span className="bp-mono" style={{ fontSize: 12, fontWeight: 700, color: C.accent, overflow: "hidden", textOverflow: "ellipsis" }}>{m.refId ? m.refId.slice(0, 10) : m.id.slice(0, 10)}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {tenantLine?.tenantId
              ? <Link to={`/wallets/${encodeURIComponent(tenantLine.tenantId)}`} title={tenantLine.tenantId}>
                  {names[tenantLine.tenantId] ?? tenantLine.tenantId}
                </Link>
              : "Platform"}
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>{KIND_LABEL[m.kind] ?? m.kind}{m.memo ? ` · ${m.memo}` : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {isReversal && <span style={tagOutline}>↺ REVERSAL</span>}
          {touchesFee && <span style={tagFee}>FEE</span>}
        </div>
        <span className="bp-mono" style={{ fontSize: 13, fontWeight: isReversal ? 800 : 700, textAlign: "right", color: isReversal ? C.danger : "var(--ink)" }}>
          {money(gross)}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.4" style={{ justifySelf: "center", transform: open ? "rotate(180deg)" : undefined }}><path d="m6 9 6 6 6-6" /></svg>
      </div>

      {open && (
        <div style={{ padding: "0 16px 14px" }}>
          <div style={{ border: "1px solid var(--line2)", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", background: "var(--accentsoft)", fontSize: 11, fontWeight: 800, color: "var(--muted)", letterSpacing: ".04em" }}>
              JOURNAL LINES — DEBITS POSITIVE, CREDITS NEGATIVE
            </div>
            {m.lines.map((l, i) => (
              <div key={i} style={{ display: "flex", gap: 10, justifyContent: "space-between", padding: "9px 14px", borderTop: "1px solid var(--line)", flexWrap: "wrap" }}>
                {l.tenantId
                  ? <Link to={`/wallets/${encodeURIComponent(l.tenantId)}`} title={l.tenantId} style={{ fontSize: 12.5, fontWeight: 700 }}>Wallet · {names[l.tenantId] ?? l.tenantId}</Link>
                  : <span style={{ fontSize: 12.5, fontWeight: 700 }}>{l.account.replace(/_/g, " ")} (platform)</span>}
                <span className="bp-mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{money(l.amount)}</span>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", padding: "9px 14px", borderTop: "1.5px solid var(--line2)", background: "var(--subtle)" }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)" }}>Σ</span>
              <span className="bp-mono" style={{ fontSize: 12.5, fontWeight: 800 }}>{money(sum)}</span>
              {sum === 0
                ? <><Tick size={13} /><span style={{ fontSize: 11, fontWeight: 700, color: C.good }}>balanced</span></>
                : <span style={{ fontSize: 11, fontWeight: 800, color: C.danger }}>DOES NOT BALANCE</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "150px 110px 1fr 110px 140px 34px", gap: 10 };
const sel: React.CSSProperties = { padding: "9px 11px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--subtle)", color: "var(--ink)", fontSize: 12.5, fontWeight: 700 };
const tagOutline: React.CSSProperties = { fontSize: 9.5, fontWeight: 800, letterSpacing: ".04em", border: "1.5px solid var(--muted)", color: "var(--muted)", borderRadius: 6, padding: "2px 6px" };
const tagFee: React.CSSProperties = { fontSize: 9.5, fontWeight: 800, letterSpacing: ".04em", background: "var(--ink)", color: "var(--bg)", borderRadius: 6, padding: "3px 6px" };
